import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, clearToken, getToken, formatApiErrorDetail } from "../lib/api";
import Seo from "../lib/seo";
import { NAV_ITEMS } from "../lib/nav";
import AllCityAdmin from "../components/admin/AllCityAdmin";

const SERIES = ["genesis", "explorations", "objects", "field"];
const pad3 = (n) => String(n).padStart(3, "0");

const emptyArt = () => ({
  slug: "", number: 6, number_label: "RCKT 006", collection: "explorations", title: "", category: "",
  year: "2026", medium: "Alcohol marker on 400gsm paper", dimensions: "", image: "", secondary_images: [],
  description: "", availability: "AVAILABLE", edition: "ORIGINAL", edition_info: "",
  published: true, featured: false, portfolio: false, display_order: 0, portfolio_order: 0,
  seo_title: "", seo_description: "", og_image: "", alt_text: "",
  home_archive: false, instagram_url: "",
});

function Toggle({ label, value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className="flex items-center justify-between w-full border hairline px-4 py-3 bg-white hover:border-[var(--ink)] transition-colors">
      <span className="font-display uppercase tracking-[0.06em] text-sm">{label}</span>
      <span className="w-11 h-6 rounded-full relative transition-colors" style={{ background: value ? "var(--ink)" : "var(--line)" }}>
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: value ? "22px" : "2px" }} />
      </span>
    </button>
  );
}

const Field = ({ label, children, hint }) => (
  <div>
    <label className="label block mb-2">{label}</label>
    {children}
    {hint && <div className="text-xs text-[var(--muted)] mt-1">{hint}</div>}
  </div>
);
const inputCls = "w-full bg-white border hairline px-3 py-2.5 outline-none focus:border-[var(--ink)] transition-colors text-sm";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");
  const [works, setWorks] = useState([]);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);   // form object or null
  const [isNew, setIsNew] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  // works filters
  const [q, setQ] = useState("");
  const [fSeries, setFSeries] = useState("all");
  const [fPub, setFPub] = useState("all");
  const [fPort, setFPort] = useState("all");
  const [sort, setSort] = useState("number");
  // import + enquiries
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [footer, setFooter] = useState({ tagline: "", instagram_url: "", email: "" });
  const [navItems, setNavItems] = useState(null);
  const [practiceList, setPracticeList] = useState([]);
  const [pEditing, setPEditing] = useState(null);
  const [pIsNew, setPIsNew] = useState(false);

  const load = useCallback(() => {
    api.get("/artworks", { params: { admin_view: true } }).then((r) => setWorks(r.data)).catch(() => {});
    api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/settings/footer").then((r) => { if (r.data?.data) setFooter((p) => ({ ...p, ...r.data.data })); }).catch(() => {});
    api.get("/settings/navigation").then((r) => setNavItems(r.data?.data?.items || {})).catch(() => {});
    api.get("/practice", { params: { admin_view: true } }).then((r) => setPracticeList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/admin"); return; }
    api.get("/auth/me").catch(() => { clearToken(); navigate("/admin"); });
    load();
  }, [navigate, load]);

  const logout = () => { clearToken(); navigate("/admin"); };
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3500); };

  // ---- editor ----
  const openNew = () => {
    const next = works.filter((w) => w.collection !== "genesis").reduce((m, w) => Math.max(m, w.number), 0) + 1;
    const f = emptyArt();
    f.number = next; f.number_label = `RCKT ${pad3(next)}`; f.slug = `rckt-${pad3(next)}`; f.display_order = next;
    setEditing(f); setIsNew(true); setView("editor");
  };
  const openEdit = (w) => {
    const { prev, next, eff_seo_title, eff_seo_description, eff_alt_text, eff_og_image, id, _id, ...rest } = w;
    setEditing({ ...emptyArt(), ...rest }); setIsNew(false); setView("editor");
  };
  const setF = (k, v) => setEditing((f) => ({ ...f, [k]: v }));

  const onNumberChange = (v) => {
    const n = Number(v);
    setEditing((f) => {
      const isGen = f.collection === "genesis";
      const nl = isGen ? `GENESIS ${pad3(n).slice(1)}` : `RCKT ${pad3(n)}`;
      const patch = { number: n };
      if (isNew) { patch.number_label = nl; patch.slug = isGen ? `genesis-${pad3(n).slice(1)}` : `rckt-${pad3(n)}`; patch.display_order = n; }
      return { ...f, ...patch };
    });
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    return data.url;
  };
  const onPrimaryUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { setF("image", await uploadImage(file)); } catch { flash("Upload failed"); } finally { setUploading(false); }
  };
  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setEditing((f) => ({ ...f, secondary_images: [...(f.secondary_images || []), ...urls] }));
    } catch { flash("Upload failed"); } finally { setUploading(false); }
  };
  const moveGallery = (i, dir) => setEditing((f) => {
    const arr = [...f.secondary_images]; const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, secondary_images: arr };
  });
  const removeGallery = (i) => setEditing((f) => ({ ...f, secondary_images: f.secondary_images.filter((_, k) => k !== i) }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...editing, number: Number(editing.number), display_order: Number(editing.display_order), portfolio_order: Number(editing.portfolio_order) };
      if (isNew) await api.post("/artworks", body);
      else await api.put(`/artworks/${editing._orig_slug || editing.slug}`, body);
      flash(isNew ? "Work created ✓" : "Work saved ✓");
      load(); setView("works"); setEditing(null);
    } catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete ${slug}? This removes it from the Archive permanently.`)) return;
    await api.delete(`/artworks/${slug}`); load();
  };
  const quickUpdate = async (w, patch) => {
    const { prev, next, eff_seo_title, eff_seo_description, eff_alt_text, eff_og_image, id, _id, ...rest } = w;
    await api.put(`/artworks/${w.slug}`, { ...rest, ...patch }); load();
  };

  const CSV_TEMPLATE = "number,title,series,year,medium,dimensions,image,availability,published,featured,portfolio\n6,Untitled,explorations,2026,Alcohol marker on 400gsm paper,150 × 210 mm,,AVAILABLE,true,false,false";
  const onCsvFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => setCsvText(String(rd.result)); rd.readAsText(f);
  };
  const runImport = async () => {
    setImportResult(null);
    try {
      const { data } = await api.post("/artworks/bulk", { csv: csvText });
      setImportResult(data); load();
    } catch (e) { flash(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const markEnquiry = async (id) => { await api.patch(`/enquiries/${id}`); load(); };
  const delEnquiry = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/enquiries/${id}`); load(); };
  const saveFooter = async () => {
    try { await api.put("/settings/footer", { data: footer }); flash("Footer saved ✓"); }
    catch (e) { flash(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setFooterField = (k, v) => setFooter((f) => ({ ...f, [k]: v }));

  // ---- practice ----
  const emptyPractice = () => ({ slug: "", title: "", subtitle: "", date: new Date().toISOString().slice(0, 10),
    number: "", category: "", tags: [], status: "draft", featured: false, order: (practiceList.length + 1),
    blocks: [], seo_title: "", seo_description: "", og_image: "" });
  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const openNewPractice = () => { setPEditing(emptyPractice()); setPIsNew(true); setView("practice-editor"); };
  const openEditPractice = (p) => {
    const { eff_seo_title, eff_seo_description, eff_og_image, id, _id, ...rest } = p;
    setPEditing({ ...emptyPractice(), ...rest, _orig: p.slug }); setPIsNew(false); setView("practice-editor");
  };
  const setP = (k, v) => setPEditing((f) => ({ ...f, [k]: v }));
  const addBlock = (type) => setPEditing((f) => ({ ...f, blocks: [...f.blocks, type === "ul" || type === "ol" ? { type, items: [""] } : { type, text: "", url: "", caption: "", alt: "" }] }));
  const setBlock = (i, patch) => setPEditing((f) => ({ ...f, blocks: f.blocks.map((b, k) => k === i ? { ...b, ...patch } : b) }));
  const moveBlock = (i, d) => setPEditing((f) => { const a = [...f.blocks]; const j = i + d; if (j < 0 || j >= a.length) return f; [a[i], a[j]] = [a[j], a[i]]; return { ...f, blocks: a }; });
  const rmBlock = (i) => setPEditing((f) => ({ ...f, blocks: f.blocks.filter((_, k) => k !== i) }));
  const uploadBlockImg = async (i, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { setBlock(i, { url: await uploadImage(file) }); } catch { flash("Upload failed"); }
  };
  const savePractice = async (e, statusOverride) => {
    e && e.preventDefault();
    const body = { ...pEditing };
    if (statusOverride) body.status = statusOverride;
    if (!body.slug) body.slug = slugify(body.title || "untitled");
    body.order = Number(body.order) || 0;
    if (typeof body.tags === "string") body.tags = body.tags.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      if (pIsNew) await api.post("/practice", body);
      else await api.put(`/practice/${pEditing._orig || pEditing.slug}`, body);
      flash("Practice saved ✓"); load(); setView("practice"); setPEditing(null);
    } catch (e2) { flash(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const delPractice = async (slug) => { if (!window.confirm(`Delete practice entry ${slug}?`)) return; await api.delete(`/practice/${slug}`); load(); };

  // ---- derived works list ----
  let list = works.filter((w) => {
    if (fSeries !== "all" && w.collection !== fSeries) return false;
    if (fPub !== "all" && String(w.published) !== fPub) return false;
    if (fPort !== "all" && String(w.portfolio) !== fPort) return false;
    if (q && !(`${w.number_label} ${w.title}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  list = [...list].sort((a, b) => sort === "title" ? a.title.localeCompare(b.title) : (a.number - b.number));

  const stat = (f) => works.filter(f).length;
  const NAV = [["dashboard", "Dashboard"], ["works", "Works"], ["import", "Import"], ["practice", "Practice"], ["allcity", "All City"], ["enquiries", "Enquiries"], ["pages", "Pages"], ["seo", "SEO"], ["settings", "Settings"]];

  const saveNavigation = async () => {
    const items = Object.fromEntries(NAV_ITEMS.map((i) => [i.key, navItems?.[i.key] !== false]));
    try { await api.put("/settings/navigation", { data: { items } }); flash("Navigation saved ✓"); }
    catch { flash("Could not save navigation"); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="admin-dashboard">
      <Seo title="RCKT Admin" noindex />
      {/* sidebar */}
      <aside className="md:w-56 md:min-h-screen border-b md:border-b-0 md:border-r hairline px-6 py-6 flex md:flex-col gap-6 md:gap-0 justify-between">
        <div>
          <Link to="/" className="font-display font-700 tracking-[0.18em] text-lg">RCKT</Link>
          <div className="label mt-1 mb-8 hidden md:block">Content system</div>
          <nav className="flex md:flex-col gap-4 md:gap-1 overflow-x-auto">
            {NAV.map(([k, l]) => (
              <button key={k} onClick={() => { setView(k); setEditing(null); }} data-testid={`admin-nav-${k}`}
                className="text-left font-display uppercase tracking-[0.06em] text-sm py-2 md:px-3 whitespace-nowrap transition-colors"
                style={{ opacity: view === k ? 1 : 0.45, background: view === k ? "var(--surface)" : "transparent" }}>
                {l}
              </button>
            ))}
          </nav>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="label hover:opacity-50 transition-opacity text-left">Sign out →</button>
      </aside>

      {/* main */}
      <main className="flex-1 px-6 md:px-12 py-8 md:py-12 max-w-[1200px]">
        {msg && <div className="mb-6 border hairline bg-white px-4 py-3 label" data-testid="admin-msg">{msg}</div>}

        {/* DASHBOARD */}
        {view === "dashboard" && (
          <div>
            <h1 className="font-display font-700 text-3xl uppercase mb-8">Dashboard</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["Total works", stat(() => true)], ["Published", stat((w) => w.published)], ["Featured", stat((w) => w.featured)], ["In portfolio", stat((w) => w.portfolio)]].map(([l, v]) => (
                <div key={l} className="border hairline bg-white p-6">
                  <div className="font-display font-700 text-4xl">{v}</div>
                  <div className="label mt-2">{l}</div>
                </div>
              ))}
            </div>
            <button onClick={openNew} data-testid="dashboard-add-work" className="mt-8 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">+ Add new work</button>
          </div>
        )}

        {/* WORKS */}
        {view === "works" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-700 text-3xl uppercase">Works</h1>
              <button onClick={openNew} data-testid="works-add-work" className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-5 py-3 hover:opacity-80 transition-opacity">+ Add new work</button>
            </div>
            <div className="flex flex-wrap gap-3 mb-6">
              <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="works-search" className={inputCls + " w-44"} />
              <select value={fSeries} onChange={(e) => setFSeries(e.target.value)} data-testid="works-filter-series" className={inputCls + " w-auto"}>
                <option value="all">All series</option>{SERIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={fPub} onChange={(e) => setFPub(e.target.value)} data-testid="works-filter-published" className={inputCls + " w-auto"}>
                <option value="all">Any status</option><option value="true">Published</option><option value="false">Draft</option>
              </select>
              <select value={fPort} onChange={(e) => setFPort(e.target.value)} data-testid="works-filter-portfolio" className={inputCls + " w-auto"}>
                <option value="all">Any portfolio</option><option value="true">In portfolio</option><option value="false">Not in portfolio</option>
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="works-sort" className={inputCls + " w-auto"}>
                <option value="number">Sort: number</option><option value="title">Sort: title</option>
              </select>
            </div>
            <div className="border hairline bg-white">
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 border-b hairline label">
                <div className="col-span-2">Number</div><div className="col-span-3">Title</div><div className="col-span-2">Series</div>
                <div className="col-span-1">Year</div><div className="col-span-1">Portfolio</div><div className="col-span-1">Published</div><div className="col-span-2 text-right">Actions</div>
              </div>
              {list.map((w) => (
                <div key={w.slug} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b hairline items-center text-sm" data-testid={`works-row-${w.slug}`}>
                  <div className="md:col-span-2 font-display">{w.number_label}</div>
                  <div className="md:col-span-3 truncate">{w.title}</div>
                  <div className="md:col-span-2 text-[var(--muted)]">{w.collection}</div>
                  <div className="md:col-span-1 text-[var(--muted)]">{w.year}</div>
                  <div className="md:col-span-1">{w.portfolio ? "●" : "○"}</div>
                  <div className="md:col-span-1">{w.published ? "●" : "○ draft"}</div>
                  <div className="md:col-span-2 flex gap-3 md:justify-end">
                    <button onClick={() => openEdit({ ...w, _orig_slug: w.slug })} data-testid={`works-edit-${w.slug}`} className="label hover:opacity-50">Edit</button>
                    <button onClick={() => del(w.slug)} data-testid={`works-delete-${w.slug}`} className="label hover:text-red-700">Delete</button>
                  </div>
                </div>
              ))}
              {list.length === 0 && <div className="px-4 py-8 label">No works match.</div>}
            </div>
          </div>
        )}

        {/* EDITOR */}
        {view === "editor" && editing && (
          <form onSubmit={save} className="max-w-3xl" data-testid="admin-editor">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-700 text-3xl uppercase">{isNew ? "Add new work" : `Edit ${editing.number_label}`}</h1>
              <button type="button" onClick={() => { setView("works"); setEditing(null); }} className="label hover:opacity-50">← Back</button>
            </div>

            <section className="mb-10">
              <div className="label mb-4 pb-2 border-b hairline">Basic information</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Archive number"><input type="number" value={editing.number} onChange={(e) => onNumberChange(e.target.value)} data-testid="editor-number" className={inputCls} /></Field>
                <Field label="Number label"><input value={editing.number_label} onChange={(e) => setF("number_label", e.target.value)} data-testid="editor-number-label" className={inputCls} /></Field>
                <Field label="Title"><input value={editing.title} onChange={(e) => setF("title", e.target.value)} data-testid="editor-title" className={inputCls} /></Field>
                <Field label="Series">
                  <select value={editing.collection} onChange={(e) => setF("collection", e.target.value)} data-testid="editor-series" className={inputCls}>
                    {SERIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Category"><input value={editing.category} onChange={(e) => setF("category", e.target.value)} className={inputCls} /></Field>
                <Field label="Year"><input value={editing.year} onChange={(e) => setF("year", e.target.value)} className={inputCls} /></Field>
                <Field label="Medium"><input value={editing.medium} onChange={(e) => setF("medium", e.target.value)} className={inputCls} /></Field>
                <Field label="Dimensions"><input value={editing.dimensions} onChange={(e) => setF("dimensions", e.target.value)} className={inputCls} /></Field>
                <div className="md:col-span-2"><Field label="Description"><textarea rows={3} value={editing.description} onChange={(e) => setF("description", e.target.value)} className={inputCls} /></Field></div>
                <Field label="URL slug (advanced)" hint={`/archive/${editing.slug}`}><input value={editing.slug} onChange={(e) => setF("slug", e.target.value)} data-testid="editor-slug" className={inputCls} /></Field>
                <Field label="Instagram URL (optional)" hint="Shows an Instagram icon on the artwork page"><input value={editing.instagram_url} onChange={(e) => setF("instagram_url", e.target.value)} data-testid="editor-instagram" className={inputCls} /></Field>
                <Field label="Availability">
                  <select value={editing.availability} onChange={(e) => setF("availability", e.target.value)} className={inputCls}>
                    <option>AVAILABLE</option><option>SOLD</option><option value="NFS">NFS</option>
                  </select>
                </Field>
              </div>
            </section>

            <section className="mb-10">
              <div className="label mb-4 pb-2 border-b hairline">Images</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label block mb-2">Primary image</label>
                  {editing.image && <img src={editing.image} alt="primary" className="w-full max-w-[220px] border hairline mb-3" />}
                  <input type="file" accept="image/*" onChange={onPrimaryUpload} data-testid="editor-primary-upload" className="text-sm" />
                  <input value={editing.image} onChange={(e) => setF("image", e.target.value)} placeholder="or paste URL" className={inputCls + " mt-2"} />
                </div>
                <div>
                  <label className="label block mb-2">Gallery images (reorderable)</label>
                  <input type="file" accept="image/*" multiple onChange={onGalleryUpload} data-testid="editor-gallery-upload" className="text-sm mb-3" />
                  <div className="space-y-2">
                    {editing.secondary_images?.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 border hairline p-2">
                        <img src={s} alt="" className="w-12 h-12 object-cover" />
                        <span className="text-xs flex-1 truncate text-[var(--muted)]">{s}</span>
                        <button type="button" onClick={() => moveGallery(i, -1)} className="label px-1">↑</button>
                        <button type="button" onClick={() => moveGallery(i, 1)} className="label px-1">↓</button>
                        <button type="button" onClick={() => removeGallery(i)} className="label px-1 hover:text-red-700">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {uploading && <div className="label mt-3">Uploading…</div>}
            </section>

            <section className="mb-10">
              <div className="label mb-4 pb-2 border-b hairline">Archive status</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Toggle label="Published" value={editing.published} onChange={(v) => setF("published", v)} testid="editor-toggle-published" />
                <Toggle label="Featured" value={editing.featured} onChange={(v) => setF("featured", v)} testid="editor-toggle-featured" />
                <Toggle label="Portfolio" value={editing.portfolio} onChange={(v) => setF("portfolio", v)} testid="editor-toggle-portfolio" />
                <Toggle label="Feature on Home Archive" value={editing.home_archive} onChange={(v) => setF("home_archive", v)} testid="editor-toggle-home" />
                <Field label="Display order"><input type="number" value={editing.display_order} onChange={(e) => setF("display_order", e.target.value)} className={inputCls} /></Field>
                <Field label="Portfolio order"><input type="number" value={editing.portfolio_order} onChange={(e) => setF("portfolio_order", e.target.value)} className={inputCls} /></Field>
              </div>
            </section>

            <section className="mb-10">
              <div className="label mb-4 pb-2 border-b hairline">SEO — leave blank to use generated defaults</div>
              <div className="grid grid-cols-1 gap-5">
                <Field label="SEO title"><input value={editing.seo_title} onChange={(e) => setF("seo_title", e.target.value)} data-testid="editor-seo-title" placeholder={`${editing.number_label} — ${editing.title || "Untitled"} | RCKT Archive`} className={inputCls} /></Field>
                <Field label="Meta description"><textarea rows={2} value={editing.seo_description} onChange={(e) => setF("seo_description", e.target.value)} placeholder={`${editing.number_label}, ${editing.title || "Untitled"}. From the RCKT archive.`} className={inputCls} /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Social sharing image (OG)"><input value={editing.og_image} onChange={(e) => setF("og_image", e.target.value)} placeholder="defaults to primary image" className={inputCls} /></Field>
                  <Field label="Image alt text"><input value={editing.alt_text} onChange={(e) => setF("alt_text", e.target.value)} placeholder={`${editing.number_label} — ${editing.title || "Untitled"}`} className={inputCls} /></Field>
                </div>
              </div>
            </section>

            <div className="flex gap-4">
              <button type="submit" data-testid="editor-save" className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">{isNew ? "Create work" : "Save changes"}</button>
              <button type="button" onClick={() => { setView("works"); setEditing(null); }} className="label">Cancel</button>
            </div>
          </form>
        )}

        {/* PORTFOLIO */}
        {view === "portfolio" && (
          <div>
            <h1 className="font-display font-700 text-3xl uppercase mb-2">Portfolio</h1>
            <p className="text-[var(--muted)] text-sm mb-8 max-w-lg">Curated from the Archive. Toggling here never removes a work from the Archive.</p>
            <div className="border hairline bg-white">
              {works.filter((w) => w.portfolio).sort((a, b) => (a.portfolio_order || 999) - (b.portfolio_order || 999)).map((w) => (
                <div key={w.slug} className="flex items-center gap-4 px-4 py-3 border-b hairline" data-testid={`portfolio-row-${w.slug}`}>
                  <img src={w.image} alt="" className="w-12 h-12 object-cover border hairline" />
                  <div className="flex-1 text-sm"><div className="font-display">{w.number_label} — {w.title}</div></div>
                  <input type="number" defaultValue={w.portfolio_order || 0} key={`${w.slug}-${w.portfolio_order}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== (w.portfolio_order || 0)) quickUpdate(w, { portfolio_order: v }); }}
                    className="w-16 border hairline px-2 py-2 text-sm text-center" title="Order" />
                  <button onClick={() => quickUpdate(w, { portfolio: false })} className="label hover:text-red-700">Remove</button>
                </div>
              ))}
              {works.filter((w) => w.portfolio).length === 0 && <div className="px-4 py-8 label">No portfolio works yet.</div>}
            </div>
          </div>
        )}

        {/* PRACTICE LIST */}
        {view === "practice" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-700 text-3xl uppercase">Practice</h1>
              <button onClick={openNewPractice} data-testid="practice-add" className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-5 py-3 hover:opacity-80 transition-opacity">+ New entry</button>
            </div>
            <div className="border hairline bg-white">
              {practiceList.map((p) => (
                <div key={p.slug} className="flex items-center gap-4 px-4 py-3 border-b hairline text-sm" data-testid={`practice-row-${p.slug}`}>
                  <div className="w-16 label">{p.order || "—"}</div>
                  <div className="flex-1 min-w-0"><div className="font-display truncate">{p.title || "(untitled)"}</div><div className="label mt-1">{p.date} · {p.status}{p.category ? ` · ${p.category}` : ""}</div></div>
                  <a href={`/practice/${p.slug}`} target="_blank" rel="noreferrer" className="label hover:opacity-50">Preview</a>
                  <button onClick={() => openEditPractice(p)} data-testid={`practice-edit-${p.slug}`} className="label hover:opacity-50">Edit</button>
                  <button onClick={() => delPractice(p.slug)} data-testid={`practice-delete-${p.slug}`} className="label hover:text-red-700">Delete</button>
                </div>
              ))}
              {practiceList.length === 0 && <div className="px-4 py-8 label">No entries yet.</div>}
            </div>
          </div>
        )}

        {/* PRACTICE EDITOR */}
        {view === "practice-editor" && pEditing && (
          <form onSubmit={(e) => savePractice(e)} className="max-w-3xl" data-testid="practice-editor">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-700 text-3xl uppercase">{pIsNew ? "New Practice entry" : "Edit entry"}</h1>
              <button type="button" onClick={() => { setView("practice"); setPEditing(null); }} className="label hover:opacity-50">← Back</button>
            </div>
            <section className="mb-8">
              <div className="label mb-4 pb-2 border-b hairline">Core information</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Title"><input value={pEditing.title} onChange={(e) => setP("title", e.target.value)} data-testid="practice-title" className={inputCls} /></Field>
                <Field label="URL slug" hint={`/practice/${pEditing.slug || "(auto)"}`}><input value={pEditing.slug} onChange={(e) => setP("slug", e.target.value)} data-testid="practice-slug" placeholder="auto from title" className={inputCls} /></Field>
                <div className="md:col-span-2"><Field label="Subtitle / deck"><input value={pEditing.subtitle} onChange={(e) => setP("subtitle", e.target.value)} className={inputCls} /></Field></div>
                <Field label="Date"><input value={pEditing.date} onChange={(e) => setP("date", e.target.value)} className={inputCls} /></Field>
                <Field label="Number / identifier"><input value={pEditing.number} onChange={(e) => setP("number", e.target.value)} className={inputCls} /></Field>
                <Field label="Category"><input value={pEditing.category} onChange={(e) => setP("category", e.target.value)} className={inputCls} /></Field>
                <Field label="Tags (comma separated)"><input value={Array.isArray(pEditing.tags) ? pEditing.tags.join(", ") : pEditing.tags} onChange={(e) => setP("tags", e.target.value)} className={inputCls} /></Field>
                <Field label="Status"><select value={pEditing.status} onChange={(e) => setP("status", e.target.value)} data-testid="practice-status" className={inputCls}><option value="draft">draft</option><option value="published">published</option></select></Field>
                <Field label="Order"><input type="number" value={pEditing.order} onChange={(e) => setP("order", e.target.value)} className={inputCls} /></Field>
              </div>
            </section>
            <section className="mb-8">
              <div className="label mb-4 pb-2 border-b hairline">Content blocks</div>
              <div className="space-y-4">
                {pEditing.blocks.map((b, i) => (
                  <div key={i} className="border hairline p-4 bg-white" data-testid={`practice-block-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="label">{b.type}</span>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => moveBlock(i, -1)} className="label">↑</button>
                        <button type="button" onClick={() => moveBlock(i, 1)} className="label">↓</button>
                        <button type="button" onClick={() => rmBlock(i)} className="label hover:text-red-700">✕</button>
                      </div>
                    </div>
                    {["paragraph", "heading", "subheading", "quote", "pullquote", "link"].includes(b.type) && (
                      <textarea rows={b.type === "paragraph" ? 4 : 2} value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} placeholder="Text" className={inputCls} />
                    )}
                    {b.type === "link" && <input value={b.url || ""} onChange={(e) => setBlock(i, { url: e.target.value })} placeholder="https://…" className={inputCls + " mt-2"} />}
                    {(b.type === "ul" || b.type === "ol") && (
                      <textarea rows={4} value={(b.items || []).join("\n")} onChange={(e) => setBlock(i, { items: e.target.value.split("\n") })} placeholder="One item per line" className={inputCls} />
                    )}
                    {b.type === "image" && (
                      <div className="space-y-2">
                        {b.url && <img src={b.url} alt="" className="max-w-[200px] border hairline" />}
                        <input type="file" accept="image/*" onChange={(e) => uploadBlockImg(i, e)} className="text-sm" />
                        <input value={b.url || ""} onChange={(e) => setBlock(i, { url: e.target.value })} placeholder="or paste image URL" className={inputCls} />
                        <input value={b.alt || ""} onChange={(e) => setBlock(i, { alt: e.target.value })} placeholder="Alt text" className={inputCls} />
                        <input value={b.caption || ""} onChange={(e) => setBlock(i, { caption: e.target.value })} placeholder="Caption" className={inputCls} />
                      </div>
                    )}
                    {(b.type === "video" || b.type === "embed") && (
                      <div className="space-y-2">
                        <input value={b.url || ""} onChange={(e) => setBlock(i, { url: e.target.value })} placeholder="Embed URL (YouTube/Vimeo)" className={inputCls} />
                        <input value={b.caption || ""} onChange={(e) => setBlock(i, { caption: e.target.value })} placeholder="Caption" className={inputCls} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {["paragraph", "heading", "subheading", "quote", "pullquote", "ul", "ol", "link", "image", "video", "divider"].map((t) => (
                  <button key={t} type="button" onClick={() => addBlock(t)} data-testid={`practice-addblock-${t}`} className="label border hairline px-3 py-2 hover:border-[var(--ink)] transition-colors">+ {t}</button>
                ))}
              </div>
            </section>
            <section className="mb-8">
              <div className="label mb-4 pb-2 border-b hairline">SEO (optional)</div>
              <div className="grid grid-cols-1 gap-4">
                <Field label="SEO title"><input value={pEditing.seo_title} onChange={(e) => setP("seo_title", e.target.value)} placeholder={`${pEditing.title || "Untitled"} — RCKT Practice`} className={inputCls} /></Field>
                <Field label="Meta description"><textarea rows={2} value={pEditing.seo_description} onChange={(e) => setP("seo_description", e.target.value)} className={inputCls} /></Field>
                <Field label="Social image URL"><input value={pEditing.og_image} onChange={(e) => setP("og_image", e.target.value)} className={inputCls} /></Field>
              </div>
            </section>
            <div className="flex gap-4">
              <button type="submit" data-testid="practice-save" className="bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">Save</button>
              <button type="button" onClick={(e) => savePractice(e, "published")} data-testid="practice-publish" className="border hairline font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:border-[var(--ink)] transition-colors">Save &amp; publish</button>
            </div>
          </form>
        )}

        {/* PAGES */}
        {view === "pages" && (
          <div>
            <h1 className="font-display font-700 text-3xl uppercase mb-8">Pages</h1>
            <div className="space-y-8">
              {pages.map((p) => (
                <div key={p.key} className="border hairline bg-white p-6" data-testid={`page-${p.key}`}>
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="font-display uppercase tracking-[0.06em]">{p.title || p.key}</div>
                    <span className="label">{p.path}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="SEO title"><input defaultValue={p.seo_title} onBlur={(e) => (p._t = e.target.value)} data-testid={`page-${p.key}-title`} className={inputCls} /></Field>
                    <Field label="Meta description"><textarea rows={2} defaultValue={p.seo_description} onBlur={(e) => (p._d = e.target.value)} data-testid={`page-${p.key}-desc`} className={inputCls} /></Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Open Graph image"><input defaultValue={p.og_image} onBlur={(e) => (p._o = e.target.value)} className={inputCls} /></Field>
                      <Field label="Canonical URL"><input defaultValue={p.canonical} onBlur={(e) => (p._c = e.target.value)} className={inputCls} /></Field>
                    </div>
                    <button onClick={async () => {
                      await api.put(`/pages/${p.key}`, { title: p.title, seo_title: p._t ?? p.seo_title, seo_description: p._d ?? p.seo_description, og_image: p._o ?? p.og_image, canonical: p._c ?? p.canonical });
                      flash(`${p.key} page saved ✓`); load();
                    }} data-testid={`page-${p.key}-save`} className="w-fit bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-xs px-6 py-3 hover:opacity-80 transition-opacity">Save page</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEO */}
        {view === "seo" && (
          <div>
            <h1 className="font-display font-700 text-3xl uppercase mb-8">Technical SEO</h1>
            <div className="space-y-4 max-w-xl text-sm">
              <div className="border hairline bg-white p-5">
                <div className="label mb-2">Sitemap</div>
                <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="underline break-all" data-testid="seo-sitemap-link">/api/sitemap.xml</a>
                <p className="text-[var(--muted)] mt-2">Auto-generated from published works, Home, Archive, Portfolio and About. Drafts are excluded.</p>
              </div>
              <div className="border hairline bg-white p-5">
                <div className="label mb-2">robots.txt</div>
                <a href="/robots.txt" target="_blank" rel="noreferrer" className="underline">/robots.txt</a>
                <p className="text-[var(--muted)] mt-2">Admin routes are disallowed and set to noindex. Draft works are noindex.</p>
              </div>
              <div className="border hairline bg-white p-5">
                <div className="label mb-3">Series metadata (auto)</div>
                {SERIES.map((s) => (
                  <div key={s} className="mb-3">
                    <div className="font-display uppercase tracking-[0.06em] text-xs">{s}</div>
                    <div className="text-[var(--muted)] text-xs mt-1">Applied automatically on /archive?series={s}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* IMPORT */}
        {view === "import" && (
          <div className="max-w-3xl">
            <h1 className="font-display font-700 text-3xl uppercase mb-3">Bulk import</h1>
            <p className="text-[var(--muted)] text-sm mb-6 leading-relaxed">
              Paste CSV or choose a .csv file — one row per work. Required column: <b>number</b>. Optional:
              title, series, year, medium, dimensions, image, availability, published, featured, portfolio, display_order,
              portfolio_order, slug, number_label. Series must be genesis / explorations / objects / field. Missing slug
              and number label are generated from the number. Existing numbers or slugs are skipped, never overwritten.
            </p>
            <div className="flex items-center gap-4 mb-4">
              <input type="file" accept=".csv,text/csv" onChange={onCsvFile} data-testid="import-file" className="text-sm" />
              <button type="button" onClick={() => setCsvText(CSV_TEMPLATE)} data-testid="import-template" className="label hover:opacity-50">Insert template</button>
            </div>
            <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="import-textarea" rows={10}
              placeholder="number,title,series,year,image,availability,published…" className={inputCls + " font-mono text-xs"} />
            <button onClick={runImport} data-testid="import-run"
              className="mt-4 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">Import works</button>
            {importResult && (
              <div className="mt-6 border hairline bg-white p-5 text-sm" data-testid="import-result">
                <div className="font-display uppercase tracking-[0.06em] mb-2">Imported {importResult.created} · Skipped {importResult.skipped.length} · Errors {importResult.errors.length}</div>
                {importResult.skipped.length > 0 && <ul className="text-[var(--muted)] mt-2 list-disc pl-5">{importResult.skipped.map((s, i) => <li key={i}>{s}</li>)}</ul>}
                {importResult.errors.length > 0 && <ul className="text-red-700 mt-2 list-disc pl-5">{importResult.errors.map((s, i) => <li key={i}>{s}</li>)}</ul>}
              </div>
            )}
          </div>
        )}

        {/* ALL CITY */}
        {view === "allcity" && <AllCityAdmin works={works} flash={flash} />}

        {/* ENQUIRIES */}
        {view === "enquiries" && (
          <div>
            <h1 className="font-display font-700 text-3xl uppercase mb-2">Enquiries</h1>
            <p className="text-[var(--muted)] text-sm mb-8">Acquisition enquiries from available works. {enquiries.filter((e) => e.status === "new").length} new.</p>
            <div className="space-y-4">
              {enquiries.map((e) => (
                <div key={e.id} className="border hairline bg-white p-5" data-testid={`enquiry-${e.id}`} style={{ opacity: e.status === "read" ? 0.6 : 1 }}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display uppercase tracking-[0.06em] text-sm">{e.artwork_label} {e.artwork_title && `— ${e.artwork_title}`}{e.status === "new" && <span className="ml-2 label" style={{ color: "var(--ink)" }}>● new</span>}</div>
                      <div className="text-sm mt-2">{e.name} · <a href={`mailto:${e.email}`} className="underline">{e.email}</a></div>
                      {e.message && <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{e.message}</p>}
                      <div className="label mt-3">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {e.status === "new" && <button onClick={() => markEnquiry(e.id)} data-testid={`enquiry-read-${e.id}`} className="label hover:opacity-50">Mark read</button>}
                      <button onClick={() => delEnquiry(e.id)} data-testid={`enquiry-delete-${e.id}`} className="label hover:text-red-700">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
              {enquiries.length === 0 && <div className="border hairline bg-white px-4 py-8 label">No enquiries yet.</div>}
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {view === "settings" && (
          <div className="max-w-2xl">
            <h1 className="font-display font-700 text-3xl uppercase mb-8">Settings</h1>

            <div className="border hairline bg-white p-6 mb-6" data-testid="settings-navigation">
              <div className="label mb-4 pb-2 border-b hairline">Navigation</div>
              <p className="text-xs text-[var(--muted)] mb-4">Choose which pages appear in the site menu and footer.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {NAV_ITEMS.map((i) => (
                  <Toggle key={i.key} label={i.label} value={navItems ? navItems[i.key] !== false : true}
                    onChange={(v) => setNavItems((p) => ({ ...(p || {}), [i.key]: v }))} testid={`nav-toggle-${i.key}`} />
                ))}
              </div>
              <button onClick={saveNavigation} data-testid="navigation-save"
                className="mt-5 w-fit bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">Save navigation</button>
            </div>

            <div className="border hairline bg-white p-6 mb-6" data-testid="settings-footer">
              <div className="label mb-4 pb-2 border-b hairline">Footer</div>
              <div className="grid grid-cols-1 gap-5">
                <Field label="Tagline"><input value={footer.tagline} onChange={(e) => setFooterField("tagline", e.target.value)} data-testid="footer-tagline-input" className={inputCls} /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Instagram URL" hint="Full https:// link"><input value={footer.instagram_url} onChange={(e) => setFooterField("instagram_url", e.target.value)} data-testid="footer-instagram-input" className={inputCls} /></Field>
                  <Field label="Contact email"><input value={footer.email} onChange={(e) => setFooterField("email", e.target.value)} data-testid="footer-email-input" className={inputCls} /></Field>
                </div>
                <button onClick={saveFooter} data-testid="footer-save" className="w-fit bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-[0.1em] text-sm px-8 py-3.5 hover:opacity-80 transition-opacity">Save footer</button>
              </div>
            </div>

            <div className="border hairline bg-white p-6 space-y-3 text-sm">
              <div className="label mb-2 pb-2 border-b hairline">Account</div>
              <div className="flex justify-between"><span className="label">Admin</span><span>{getToken() ? "Signed in" : "—"}</span></div>
              <div className="flex justify-between"><span className="label">Site</span><span className="truncate">{window.location.origin}</span></div>
              <button onClick={logout} className="mt-4 label hover:opacity-50">Sign out →</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
